import { useState, useCallback, useEffect } from 'react'
import { type WindowWithEditor } from '../../../../utils/WindowWithEditor'

/**
 * Hook for tracking and managing text selection in the BlockNote editor.
 * Automatically updates when the user selects text in the editor and provides
 * the selected text content and line count for use in AI interactions.
 */
export function useEditorSelection() {
  const [selectedText, setSelectedText] = useState<string | null>(null)
  const [selectedLineCount, setSelectedLineCount] = useState<number>(0)

  const updateSelectedText = useCallback(() => {
    const editor = (window as WindowWithEditor).editor
    if (!editor) {
      setSelectedText(null)
      setSelectedLineCount(0)
      return
    }
    
    const selection = editor.getSelectedText()
    if (selection && selection.trim()) {
      setSelectedText(selection)
      const lineCount = selection.split('\n').length
      setSelectedLineCount(lineCount)
    } else {
      setSelectedText(null)
      setSelectedLineCount(0)
    }
  }, [])

  useEffect(() => {
    const editor = (window as WindowWithEditor).editor
    if (!editor) return

    // Update selection on mount
    updateSelectedText()

    // Listen for selection changes
    const handleSelectionUpdate = () => {
      updateSelectedText()
    }

    // Subscribe to selection updates
    editor._tiptapEditor.on('selectionUpdate', handleSelectionUpdate)
    
    return () => {
      editor._tiptapEditor.off('selectionUpdate', handleSelectionUpdate)
    }
  }, [updateSelectedText])

  return {
    selectedText,
    selectedLineCount,
    updateSelectedText
  }
}